// src/components/bar/BarOrderDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import ProductList from './ProductList';
import ProductDetail from './ProductDetail';
import OrderPreparingScreen from './OrderPreparingScreen';
import PickupCompleteScreen from './PickupCompleteScreen';
import { ProfileForm } from '../profile';
import PaymentPopup from '../payment/PaymentPopup';
import OrderFooter from './OrderFooter';
import { getDatabase, ref, onValue, set, push, update, get } from 'firebase/database';
import { mainDatabase, contentUsersDatabase } from '../../firebase'; // 更新されたインポート
import './paymentStatusAnimation.css'; // アニメーション用のCSSをインポート
import shared from '../../styles/shared.module.css';
import styles from './Bar.module.css';

const BarOrderDashboard = () => {
  const { currentUser, logout, getUserProfile } = useAuth();
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('main');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsFromDatabase, setProductsFromDatabase] = useState(true); // Content DB から取得成功時 true
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [orderingProduct, setOrderingProduct] = useState(null); // 注文中の商品
  const [currentOrderId, setCurrentOrderId] = useState(null); // 現在の注文ID
  const [orderStatus, setOrderStatus] = useState(null); // 注文ステータス (preparing, ready, completed)
  const [isUserAdmin, setIsUserAdmin] = useState(false); // ユーザーが管理者かどうか
  const [showPaymentPopup, setShowPaymentPopup] = useState(false); // 支払いポップアップの表示状態
  const [pickupCompleteProduct, setPickupCompleteProduct] = useState(null); // 受け取り完了画面で表示する商品

  // リスナーの登録解除用の参照を保持
  const orderListenerRef = useRef(null);

  // ポップアップ・モーダル表示中は背景のスクロールを無効にする
  useEffect(() => {
    const hasOpenOverlay = showPopup || showPaymentPopup || showNicknameModal;
    if (hasOpenOverlay) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showPopup, showPaymentPopup, showNicknameModal]);

  // 商品データを正規化する共通処理
  const normalizeProducts = (data) => {
    if (!data) return [];
    return Object.keys(data).map(key => ({
      id: key,
      ...data[key],
      image: data[key].imageUrl || data[key].image,
      isHidden: data[key].status === 'hidden' || data[key].isHidden === true,
      type: data[key].category || data[key].type || '未分類',
      sortOrder: data[key].sortOrder !== undefined ? data[key].sortOrder : 999
    })).sort((a, b) => {
      const sortOrderA = a.sortOrder !== undefined ? a.sortOrder : 999;
      const sortOrderB = b.sortOrder !== undefined ? b.sortOrder : 999;
      if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;
      return (a.id || '').localeCompare(b.id || '');
    });
  };

  // Firebase から商品データを読み込む（Content DB優先、失敗時はMain DBフォールバック）
  useEffect(() => {
    const contentRef = ref(contentUsersDatabase, 'products/items');
    const mainRef = ref(mainDatabase, 'products/items');

    const applyProducts = (data, fromDatabase) => {
      setProducts(normalizeProducts(data));
      setProductsFromDatabase(fromDatabase);
      setLoading(false);
    };

    const tryMainDbFallback = () => {
      onValue(mainRef, (snapshot) => {
        applyProducts(snapshot.val(), false);
      }, (err) => {
        console.error('Main DB からの商品取得も失敗:', err);
        setProducts([]);
        setProductsFromDatabase(false);
        setLoading(false);
      });
    };

    const unsubscribe = onValue(contentRef, (snapshot) => {
      const data = snapshot.val();
      applyProducts(data, true);
    }, (err) => {
      console.error('Content DB からの商品取得に失敗、Main DB を試行:', err);
      tryMainDbFallback();
    });

    return () => unsubscribe();
  }, []);

  // 注文データを取得・監視する関数
  const setupOrderListener = (orderId) => {
    console.log(`BarOrderDashboard: 注文 ${orderId} のリアルタイム監視を開始`);

    // 以前のリスナーがあれば解除
    if (orderListenerRef.current) {
      orderListenerRef.current();
      orderListenerRef.current = null;
    }

    const db = getDatabase();
    const orderRef = ref(db, `orders/${orderId}`);

    // リアルタイム監視を設定
    const unsubscribe = onValue(orderRef, (snapshot) => {
      const orderData = snapshot.val();
      if (orderData) {
        console.log('BarOrderDashboard: 注文データ更新を受信:', orderData);

        // 管理者またはユーザーが受け取り完了にした場合はフッターを消し、受け取り完了画面へ遷移
        if (orderData.isPickedUp) {
          console.log('BarOrderDashboard: 注文が受け取り済み（管理者/ユーザー）のためフッターを解除し、完了画面へ遷移します');
          const productData = {
            id: orderData.productId,
            name: orderData.productName,
            price: orderData.price,
            image: orderData.productImage,
            description: orderData.productDescription || ''
          };
          setOrderingProduct(null);
          setCurrentOrderId(null);
          setOrderStatus(null);
          setPickupCompleteProduct(productData);
          setCurrentScreen('pickupComplete');
          if (orderListenerRef.current) {
            orderListenerRef.current();
            orderListenerRef.current = null;
          }
          return;
        }

        // 注文中の商品情報を設定
        const productData = {
          id: orderData.productId,
          name: orderData.productName,
          price: orderData.price,
          image: orderData.productImage,
          description: orderData.productDescription || ''
        };

        setOrderingProduct(productData);

        // 注文ステータスを更新（注文中 / 作り始めました / 受け取る）
        if (orderData.isReady) {
          console.log('BarOrderDashboard: 注文が準備完了状態に更新されました');
          setOrderStatus('ready'); // 受け取る
        } else if (orderData.status === 'preparation') {
          console.log('BarOrderDashboard: 注文は製作中です');
          setOrderStatus('preparation'); // 作り始めました
        } else {
          console.log('BarOrderDashboard: 注文を受け付けました');
          setOrderStatus('pending'); // 注文中
        }
      } else {
        console.log('BarOrderDashboard: 注文データが見つかりません');
        setOrderingProduct(null);
        setOrderStatus(null);
      }
    }, (error) => {
      console.error('BarOrderDashboard: 注文データの取得エラー:', error);
    });

    // リスナーの参照を保存
    orderListenerRef.current = unsubscribe;

    return unsubscribe;
  };

  // コンポーネントのアンマウント時にリスナーを解除
  useEffect(() => {
    return () => {
      if (orderListenerRef.current) {
        console.log('BarOrderDashboard: リスナーを解除します');
        orderListenerRef.current();
      }
    };
  }, []);

  // ユーザープロフィールを取得（初回・プロフィール保存後の再取得に使用）
  const refetchUserProfile = useCallback(async (skipModalOpen = false) => {
    if (!currentUser) return;
    try {
      const newUserRef = ref(contentUsersDatabase, `users/profiles/${currentUser.uid}`);
      const newUserSnapshot = await get(newUserRef);
      let profileData = newUserSnapshot.exists() ? newUserSnapshot.val() : null;

      // Content DB にプロフィールがない場合、Main DB から取得して Content DB に同期作成
      if (!profileData) {
        const mainProfile = await getUserProfile(currentUser.uid);
        if (mainProfile) {
          const timestamp = new Date().toISOString();
          profileData = {
            displayName: mainProfile.nickname || mainProfile.email || currentUser.email || '',
            lastName: mainProfile.lastName ?? '',
            firstName: mainProfile.firstName ?? '',
            email: mainProfile.email || currentUser.email || '',
            paymentInfo: {
              currentStatus: mainProfile.paymentStatus ?? 'unpaid',
              completedAt: mainProfile.paymentCompletedAt ?? null,
              requestedAt: mainProfile.paymentRequestedAt ?? null,
              amount: mainProfile.paymentAmount ?? 0
            },
            createdAt: mainProfile.createdAt ?? timestamp,
            updatedAt: timestamp
          };
          await set(newUserRef, profileData);
        }
      }

      if (profileData) {
        const mainProfile = await getUserProfile(currentUser.uid);
        // 支払い状態は Main DB を優先し、なければ Content DB の paymentInfo を使用（リセットと表示の一貫性のため）
        const paymentStatusFromMain = mainProfile?.paymentStatus;
        const paymentStatusFromContent = profileData.paymentInfo?.currentStatus;
        const resolvedPaymentStatus = paymentStatusFromMain ?? paymentStatusFromContent ?? 'unpaid';

        const compatibleProfile = {
          ...profileData,
          nickname: mainProfile != null ? (mainProfile.nickname ?? '') : (profileData.displayName ?? ''),
          lastName: mainProfile?.lastName ?? profileData.lastName,
          firstName: mainProfile?.firstName ?? profileData.firstName,
          paymentStatus: resolvedPaymentStatus,
          paymentCompletedAt: mainProfile?.paymentCompletedAt ?? profileData.paymentInfo?.completedAt,
          paymentRequestedAt: mainProfile?.paymentRequestedAt ?? profileData.paymentInfo?.requestedAt
        };
        setUserProfile(compatibleProfile);
        const hasFullName = compatibleProfile.lastName?.trim() && compatibleProfile.firstName?.trim();
        if (!skipModalOpen && (!compatibleProfile.nickname || !hasFullName)) {
          setShowNicknameModal(true);
        }
        const hasActiveOrder = profileData.lastActiveOrderId;
        if (hasActiveOrder) {
          setCurrentOrderId(profileData.lastActiveOrderId);
          setupOrderListener(profileData.lastActiveOrderId);
        }
        const adminStatus = profileData.role === 'admin';
        setIsUserAdmin(adminStatus);
      } else {
        console.error('新DB・旧DBの両方にユーザープロファイルが見つかりません:', currentUser.uid);
        alert('プロフィール情報が見つかりません。管理者に連絡してください。');
      }
    } catch (error) {
      console.error('プロフィール取得エラー:', error);
      alert(`プロフィール情報の取得に失敗しました: ${error.message}`);
    }
  }, [currentUser, getUserProfile]);

  useEffect(() => {
    refetchUserProfile();
  }, [refetchUserProfile]);

  // 管理者が支払いを承認したときに、ユーザー画面で即時「支払い済み」に切り替わるよう Main DB をリアルタイム購読
  useEffect(() => {
    if (!currentUser) return;
    const db = getDatabase();
    const mainUserRef = ref(db, `users/${currentUser.uid}`);
    const unsubscribe = onValue(mainUserRef, (snapshot) => {
      const mainData = snapshot.val();
      if (!mainData) return;
      setUserProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          paymentStatus: mainData.paymentStatus ?? prev.paymentStatus,
          paymentCompletedAt: mainData.paymentCompletedAt ?? prev.paymentCompletedAt,
          paymentRequestedAt: mainData.paymentRequestedAt ?? prev.paymentRequestedAt
        };
      });
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 商品をクリックしたときの処理
  const handleProductClick = (product) => {
    // 広告商品の場合はポップアップを表示しない
    if (product.type === 'ad' || product.isAd === true) {
      // ProductList コンポーネント内で処理されるので何もしない
      return;
    }
    // 通常の商品の場合は従来通りポップアップを表示
    setSelectedProduct(product);
    setShowPopup(true);
  };

  // 注文処理
  const handleOrder = async () => {
    if (!selectedProduct || !currentUser) {
      console.error('商品またはユーザー情報がありません');
      return;
    }

    // 支払い状態をチェック
    if (!userProfile || userProfile.paymentStatus !== 'completed') {
      alert('注文するには先に支払いを完了させる必要があります。');
      setShowPopup(false);
      setShowPaymentPopup(true); // 支払いポップアップを表示
      return;
    }

    // 本名（姓・名）が未登録の場合は注文不可
    if (!userProfile.lastName?.trim() || !userProfile.firstName?.trim()) {
      alert('注文するにはプロフィールで本名（姓・名）を登録してください。');
      setShowPopup(false);
      setShowNicknameModal(true);
      return;
    }

    try {
      setLoading(true);
      const db = mainDatabase; // 注文は引き続きメインDBに保存

      // 新しい注文IDを生成
      const newOrderRef = push(ref(db, 'orders'));
      const newOrderId = newOrderRef.key;

      if (!newOrderId) {
        throw new Error('注文IDの生成に失敗しました');
      }

      console.log(`新しい注文ID生成: ${newOrderId}`);

      // 管理者の注文管理で誰が注文したかわかるよう、本名（姓・名）を保存。未設定時はニックネーム→メール（Content DB の displayName は使わない）
      const fullName = [userProfile?.lastName, userProfile?.firstName].filter(Boolean).join(' ').trim();
      let userName = fullName || userProfile?.nickname || currentUser?.email || 'ゲスト';
      const userNickname = (userProfile?.nickname || '').trim() || (currentUser?.email || '');

      // 注文データを作成
      const orderData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userName,
        userNickname: userNickname || 'ゲスト', // 店舗状況ページ用（本名非表示。非管理者も orders から表示可能）
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        productImage: selectedProduct.image || selectedProduct.imageUrl || '',
        productDescription: selectedProduct.description || '',
        price: selectedProduct.price,
        orderedAt: new Date().toISOString(),
        isReady: false,
        isPickedUp: false,
        status: 'preparing'
      };

      console.log('注文データ作成:', orderData);

      // データベースに注文情報を保存
      console.log(`注文データを ${newOrderId} に保存中...`);
      await set(ref(db, `orders/${newOrderId}`), orderData);
      console.log('注文データの保存が完了しました');

      // 新DBのユーザー情報を更新
      console.log(`ユーザー ${currentUser.uid} の注文IDを更新中...`);

      // 新DBのユーザー情報のみ更新
      const timestamp = new Date().toISOString();
      await update(ref(contentUsersDatabase, `users/profiles/${currentUser.uid}`), {
        lastActiveOrderId: newOrderId,
        lastOrderTime: timestamp,
        updatedAt: timestamp
      });

      console.log('ユーザー情報の更新が完了しました');

      // 注文履歴にも保存
      console.log('注文履歴に保存中...');
      await set(ref(db, `orderHistory/${currentUser.uid}/${newOrderId}`), {
        ...orderData,
        orderId: newOrderId
      });
      console.log('注文履歴の保存が完了しました');

      // 注文IDを設定
      setCurrentOrderId(newOrderId);

      // 注文のリアルタイム監視を開始
      setupOrderListener(newOrderId);

      // ポップアップを閉じて準備中画面に遷移
      setShowPopup(false);
      setCurrentScreen('preparing');

      console.log('注文処理が完了しました');
    } catch (error) {
      console.error('注文処理エラー:', error);
      alert(`注文処理中にエラーが発生しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // メニュー画面に戻る
  const handleBackToMenu = () => {
    setCurrentScreen('main');
    // 注文中の商品情報はそのまま保持
  };

  // 受け取り完了画面からメニューに戻る
  const handleBackFromPickupComplete = () => {
    setCurrentScreen('main');
    setPickupCompleteProduct(null);
  };

  // フッターがクリックされたとき
  const handleFooterClick = () => {
    if (orderingProduct) {
      setSelectedProduct(orderingProduct);
      setShowPopup(true);
    }
  };

  // 受け取り完了処理（完了時はアラートは使わず、PickupCompleteScreen へ画面遷移する）
  const handlePickup = async () => {
    if (!currentOrderId) {
      console.error('現在の注文IDがありません');
      return;
    }

    try {
      setLoading(true);
      console.log(`注文 ${currentOrderId} を受け取り済みにマーク中...`);

      const db = mainDatabase;
      const orderRef = ref(db, `orders/${currentOrderId}`);
      const timestamp = new Date().toISOString();

      // 注文データを更新
      await update(orderRef, {
        isPickedUp: true,
        status: 'completed',
        pickedUpAt: timestamp
      });
      console.log('注文データを受け取り済みに更新しました');

      // 注文履歴も更新
      const historyRef = ref(db, `orderHistory/${currentUser.uid}/${currentOrderId}`);
      await update(historyRef, {
        isPickedUp: true,
        status: 'completed',
        pickedUpAt: timestamp
      });
      console.log('注文履歴を更新しました');

      // 新DBのユーザー情報のみ更新
      console.log(`ユーザー ${currentUser.uid} の現在の注文IDをクリア中...`);

      // 新DBのユーザー情報を更新
      await update(ref(contentUsersDatabase, `users/profiles/${currentUser.uid}`), {
        lastActiveOrderId: null,
        lastPickupAt: timestamp,
        updatedAt: timestamp
      });

      console.log('ユーザーデータの更新が完了しました');

      // 表示用に商品情報を保持してから state をリセット
      const productForComplete = orderingProduct;
      setOrderingProduct(null);
      setCurrentOrderId(null);
      setOrderStatus(null);

      // 以前のリスナーがあれば解除
      if (orderListenerRef.current) {
        orderListenerRef.current();
        orderListenerRef.current = null;
      }

      // アラートは使わず、受け取り完了画面に切り替え
      setPickupCompleteProduct(productForComplete);
      setCurrentScreen('pickupComplete');
      console.log('受け取り処理が完了しました');
    } catch (error) {
      console.error('受け取り処理エラー:', error);
      alert(`受け取り処理中にエラーが発生しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ハンバーガーメニューの切り替え
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // ニックネーム設定モーダルを開く
  const openNicknameModal = () => {
    setShowNicknameModal(true);
    setMenuOpen(false);
  };

  // 支払いページへ移動
  const navigateToPayment = () => {
    setShowPaymentPopup(true);
    setMenuOpen(false);
  };

  // 店舗状況ページへ移動
  const navigateToStoreStatus = () => {
    navigate('/store-status');
    setMenuOpen(false);
  };

  // 管理者設定コンソールへ移動
  const navigateToSettingsConsole = () => {
    navigate('/settings');
    setMenuOpen(false);
  };
  // 管理者注文コンソールへ移動
  const navigateToAdminConsole = () => {
    navigate('/admin/orders');
    setMenuOpen(false);
  };

  // 商品管理コンソールへ移動（新規追加）
  const navigateToProductManager = () => {
    navigate('/admin/products');
    setMenuOpen(false);
  };

  // 支払い状態が更新されたときの処理
  const handlePaymentUpdate = (updatedPaymentInfo) => {
    setUserProfile(prev => ({
      ...prev,
      ...updatedPaymentInfo
    }));
  };

  // メニュー外をクリックした時にメニューを閉じる
  const handleClickOutside = () => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  if (currentScreen === 'pickupComplete') {
    return (
      <PickupCompleteScreen
        product={pickupCompleteProduct}
        onBackToMenu={handleBackFromPickupComplete}
      />
    );
  }

  if (currentScreen === 'preparing') {
    return (
      <OrderPreparingScreen
        selectedProduct={orderingProduct}
        orderId={currentOrderId}
        onBackToMenu={handleBackToMenu}
        onPickup={handlePickup}
      />
    );
  }

  // 支払い状態に応じたスタイルを生成
  const getPaymentStatusStyle = () => {
    if (!userProfile) return '';

    switch (userProfile.paymentStatus) {
      case 'completed':
        return styles.paymentStatusCompleted;
      case 'pending':
        return styles.paymentStatusPending;
      default:
        return styles.paymentStatusUnpaid;
    }
  };

  // 支払い状態のテキストを取得
  const getPaymentStatusText = () => {
    if (!userProfile) return '未支払い';

    switch (userProfile.paymentStatus) {
      case 'completed':
        return '支払い済み';
      case 'pending':
        return '支払い確認中';
      default:
        return '未支払い';
    }
  };

  // 支払いアラートのスタイルを取得
  const getPaymentAlertClass = () => {
    return userProfile?.paymentStatus === 'pending'
      ? `${styles.paymentAlert} ${styles.paymentAlertPending}`
      : `${styles.paymentAlert} ${styles.paymentAlertUnpaid}`;
  };

  // 支払いアラートのタイトルスタイルを取得
  const getPaymentAlertTitleClass = () => {
    return userProfile?.paymentStatus === 'pending'
      ? styles.paymentAlertTitlePending
      : styles.paymentAlertTitleUnpaid;
  };

  // 支払いアラートのボタンスタイルを取得
  const getPaymentAlertButtonClass = () => {
    return userProfile?.paymentStatus === 'pending'
      ? styles.paymentAlertButtonPending
      : styles.paymentAlertButtonUnpaid;
  };

  return (
    <div className={styles.container} onClick={handleClickOutside}>
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.brandWrapper}>
            <h1 className={styles.brandTitle}>
              {userProfile?.paymentStatus === 'completed' ? (
                <div className={styles.brandTitleWithStar}>
                  BANNAI'S BAR
                  <span className="rotating-star ml-2">★</span>
                </div>
              ) : (
                "BANNAI'S BAR"
              )}
            </h1>
            {userProfile && (
              <span className={`${styles.paymentStatus} ${getPaymentStatusStyle()}`}>
                {getPaymentStatusText()}
              </span>
            )}
          </div>
          <div className={styles.menuButtonWrapper}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu();
              }}
              className={styles.menuButton}
            >
              <svg className={styles.menuIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>

            {menuOpen && (
              <div
                className={styles.menuDropdown}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.menuHeader}>
                  <p className={styles.menuUserName}>
                    {userProfile?.nickname || currentUser?.email || 'ゲスト'}
                  </p>
                  <p className={`${styles.menuUserStatus} ${getPaymentStatusStyle()}`}>
                    {getPaymentStatusText()}
                  </p>
                </div>
                <button
                  onClick={navigateToPayment}
                  className={styles.menuItem}
                >
                  お支払い
                </button>
                <button
                  onClick={openNicknameModal}
                  className={styles.menuItem}
                >
                  プロフィール設定
                </button>
                <button
                  onClick={navigateToStoreStatus}
                  className={styles.menuItem}
                >
                  店舗状況
                </button>

                {isUserAdmin && (
                  <>
                    <button
                      onClick={navigateToSettingsConsole}
                      className={`${styles.menuItem} ${styles.menuItemAdmin}`}
                    >
                      支払い管理（管理者）
                    </button>
                    <button
                      onClick={navigateToAdminConsole}
                      className={`${styles.menuItem} ${styles.menuItemAdmin}`}
                    >
                      注文管理（管理者）
                    </button>
                    <button
                      onClick={navigateToProductManager}
                      className={`${styles.menuItem} ${styles.menuItemAdmin}`}
                    >
                      メニュー管理（管理者）
                    </button>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className={`${styles.menuItem} ${styles.menuItemLogout}`}
                >
                  ログアウト
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className={styles.mainContent}>
        {loading ? (
          <div className={shared.loadingSpinnerInline}>
            <div className={shared.spinner}></div>
          </div>
        ) : (
          <>
            {/* 支払い状態に関する通知 */}
            {userProfile && userProfile.paymentStatus !== 'completed' && (
              <div className={getPaymentAlertClass()}>
                <svg className={styles.paymentAlertIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className={styles.paymentAlertContent}>
                  <p className={`${styles.paymentAlertTitle} ${getPaymentAlertTitleClass()}`}>
                    {userProfile.paymentStatus === 'pending' ?
                      'お支払い確認中です' :
                      'お支払いが必要です'}
                  </p>
                  <p className={styles.paymentAlertDescription}>
                    {userProfile.paymentStatus === 'pending' ?
                      'スタッフによる確認をお待ちください。確認後にドリンクを注文できるようになります。' :
                      'ドリンクの注文には先にお支払いが必要です。お支払いページに移動して支払い手続きを行ってください。'}
                  </p>
                  <button
                    onClick={() => setShowPaymentPopup(true)}
                    className={`${styles.paymentAlertButton} ${getPaymentAlertButtonClass()}`}
                  >
                    お支払いページへ
                  </button>
                </div>
              </div>
            )}

            {orderingProduct && (
              <div className={styles.orderBanner}>
                <p className={styles.orderBannerTitle}>
                  <span className={styles.orderBannerTitleBold}>注文中:</span> {orderingProduct.name}
                </p>
                <p className={styles.orderBannerDescription}>
                  他の商品の詳細も確認できますが、現在の注文が完了するまで新たに注文することはできません。
                </p>
              </div>
            )}

            {products.length > 0 ? (
              <ProductList
                products={products}
                productsFromDatabase={productsFromDatabase}
                onProductClick={handleProductClick}
                orderingProduct={orderingProduct}
                onShowPayment={() => setShowPaymentPopup(true)}
              />
            ) : (
              <div className={styles.noProducts}>
                <p className={styles.noProductsText}>商品が見つかりませんでした。</p>
              </div>
            )}
          </>
        )}
      </main>

      {showPopup && selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          productsFromDatabase={productsFromDatabase}
          onClose={() => setShowPopup(false)}
          onOrder={handleOrder}
          isOrdering={orderingProduct && orderingProduct.id === selectedProduct.id}
          hasActiveOrder={orderingProduct !== null}
          paymentStatus={userProfile?.paymentStatus || 'unpaid'}
          hasFullName={!!(userProfile?.lastName?.trim() && userProfile?.firstName?.trim())}
          onShowPayment={() => setShowPaymentPopup(true)}
          onShowProfile={() => { setShowPopup(false); setShowNicknameModal(true); }}
        />
      )}

      {/* プロフィール設定モーダル */}
      {showNicknameModal && (
        <div className={styles.profileModal}>
          <div className={styles.profileModalContent}>
            <ProfileForm
              isModal={true}
              onClose={() => setShowNicknameModal(false)}
              onProfileUpdated={() => refetchUserProfile(true)}
            />
          </div>
        </div>
      )}

      {/* 支払いポップアップ */}
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
        userProfile={userProfile}
        onPaymentUpdate={handlePaymentUpdate}
      />

      {/* 注文中の商品を表示するフッター */}
      {currentScreen === 'main' && orderingProduct && (
        <OrderFooter
          orderingProduct={orderingProduct}
          orderId={currentOrderId}
          orderStatus={orderStatus}
          onFooterClick={handleFooterClick}
          onPickup={handlePickup}
        />
      )}
    </div>
  );
};

export default BarOrderDashboard;